/**
 * One order, replayed: the map with the car moving on it, the controls of a player, the
 * timeline with its chapters, and beside them what was expected, what happened and the calls.
 *
 * The clock is the replay's own. It runs on the axis — real time with the quiet stretches
 * folded — so a pre-booking taken two days ahead plays its ride rather than its two days. At
 * speeds a person can follow, a recorded call plays in step with the car; faster than that it
 * is silent, and one click hears it alone.
 *
 * The keys are the player's, never the console's: Space, the arrows, Shift and the arrows for
 * chapters, minus and plus for speed, F to follow. The digits stay the console's views.
 */

import {
  lazy,
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useTranslation } from 'react-i18next';

import { callsApi } from '../calls/api';
import { useCancellationReasonNames } from '../orders/cancellationReasons';
import { isRiderReason } from '../orders/history';
import { stateTone } from '../orders/states';
import { describe } from '../settings/errors';
import { useTenantFormats } from '../shell/formats';
import {
  Banner,
  Button,
  Card,
  CardHeader,
  EmptyState,
  Icon,
  Pill,
  Segmented,
  Sub,
  cx,
} from '../ui';
import { downloadJourney, reportsApi } from './api';
import { isFresh, type HeldLink } from './callArchive';
import {
  AUDIBLE_UP_TO,
  SPEEDS,
  STEP_SECONDS,
  actionFor,
  axisOf,
  carAt,
  chapterAt,
  distanceWords,
  elapsed,
  eventReason,
  eventTone,
  faster,
  fromAxis,
  nextTarget,
  previousTarget,
  problemTone,
  recordingOffset,
  skipTargets,
  slower,
  toAxis,
  type JourneyCall,
  type JourneyDetail,
  type JourneyEvent,
  sourceWord,
} from './journey';
import { JourneyOrder } from './JourneyOrder';
import { JourneySheet } from './JourneySheet';
import { JourneyFare } from './JourneyFare';
import { JourneySpeed, SpeedChart } from './JourneySpeed';
import { JourneyTimeline } from './JourneyTimeline';

const JourneyMap = lazy(async () => ({
  default: (await import('./JourneyMap')).JourneyMap,
}));

/** What a first press of play does: a twenty-minute ride in two minutes. */
const FIRST_SPEED = 10;

/**
 * Where the seven cards go, by the width of the screen.
 *
 * On a wide screen there are three columns, each its own stack running to the foot of the
 * window: the map with the player's controls and the speed chart under it, half the width;
 * *What happened* over the order panel; *Expected and actual* over the fare over the speed's
 * figures. The events are read beside the car they describe, and the controls sit under the
 * map they drive rather than across the page. The map takes what height the rest leave it.
 *
 * Narrower, a quarter is too thin for a line of events, so the three columns dissolve
 * (`contents`) and their cards flow into two: the map and the controls full width, then the
 * events beside the order, the measures beside the fare, and the speed's figures across. The reading order is the same
 * at every width, because the cards are never reordered — only the columns come and go.
 */
const LAYOUT = {
  page: 'flex flex-col gap-3',
  grid: 'grid grid-cols-2 gap-3 items-stretch min-[1800px]:grid-cols-[2fr_1fr_1fr] min-[1800px]:h-[calc(100dvh-200px)] min-[1800px]:min-h-[780px]',
  column:
    'contents min-[1800px]:flex min-[1800px]:flex-col min-[1800px]:gap-3 min-[1800px]:min-h-0',
  map: 'col-span-2 flex flex-col min-[1800px]:flex-1 min-[1800px]:min-h-0',
  mapHeight: 'h-[48vh] min-[1800px]:h-auto min-[1800px]:flex-1',
  controls: 'col-span-2 min-[1800px]:shrink-0',
  events: 'min-[1800px]:flex-1 min-[1800px]:min-h-0',
  order: 'min-[1800px]:shrink-0 min-[1800px]:max-h-[58%]',
  measures: 'min-[1800px]:flex-1 min-[1800px]:min-h-0',
  fare: 'min-[1800px]:shrink-0',
  speed: 'col-span-2 min-[1800px]:col-span-1 min-[1800px]:shrink-0',
} as const;

export function JourneyPlayer({
  orderId,
  startAt,
  onBack,
  onMoment,
  onFail,
}: {
  orderId: string;
  /** Seconds past the start to open on, from the address. */
  startAt: number | null;
  onBack: () => void;
  /** The playhead came to rest: seconds past the start, for the address. */
  onMoment: (seconds: number) => void;
  onFail: (problem: string | null) => void;
}) {
  const { t } = useTranslation();
  const [journey, setJourney] = useState<JourneyDetail | null>(null);
  const [missing, setMissing] = useState(false);
  const [asked, setAsked] = useState(0);

  useEffect(() => {
    let cancelled = false;

    onFail(null);

    reportsApi
      .journey(orderId)
      .then((answer) => {
        if (!cancelled) setJourney(answer);
      })
      .catch((error: unknown) => {
        if (cancelled) return;

        setMissing(true);
        onFail(describe(error, t, 'settings.couldNotLoad'));
      });

    return () => {
      cancelled = true;
    };
  }, [orderId, onFail, t, asked]);

  if (journey === null) {
    return (
      <>
        <BackRow onBack={onBack} />
        {!missing && (
          <Card>
            <EmptyState>{t('app.loading')}</EmptyState>
          </Card>
        )}
      </>
    );
  }

  // Keyed by whether there is a journey yet, so the one that arrives after "being written"
  // starts its own clock rather than inheriting the empty one.
  return (
    <Replay
      key={`${journey.orderId}-${journey.legs.length}`}
      journey={journey}
      startAt={startAt}
      onBack={onBack}
      onMoment={onMoment}
      onAskAgain={() => setAsked((n) => n + 1)}
    />
  );
}

function BackRow({ onBack }: { onBack: () => void }) {
  const { t } = useTranslation();

  return (
    <div>
      <Button size="sm" onClick={onBack}>
        <Icon name="back" small />
        {t('reports.journeys.player.back')}
      </Button>
    </div>
  );
}

function Replay({
  journey,
  startAt,
  onBack,
  onMoment,
  onAskAgain,
}: {
  journey: JourneyDetail;
  startAt: number | null;
  onBack: () => void;
  onMoment: (seconds: number) => void;
  /** Fetch the journey again — for one that was still being written. */
  onAskAgain: () => void;
}) {
  const { t } = useTranslation();
  const formats = useTenantFormats();

  const axis = useMemo(() => axisOf(journey), [journey]);
  const targets = useMemo(() => skipTargets(journey), [journey]);

  const [position, setPosition] = useState(() =>
    startAt === null ? 0 : toAxis(axis, axis.start + startAt * 1000),
  );
  const [playing, setPlaying] = useState(false);
  const [speed, setSpeed] = useState(FIRST_SPEED);
  const [follow, setFollow] = useState(false);

  const real = fromAxis(axis, position);
  const car = carAt(journey.legs, real);
  const chapter = journey.chapters[chapterAt(journey.chapters, real)] ?? null;
  const hasTrack = journey.legs.some((leg) => leg.points.length > 0);

  // The clock. Read through refs so the frame loop is started once per press of play.
  const at = useRef(position);
  const rate = useRef(speed);

  useEffect(() => {
    at.current = position;
    rate.current = speed;
  });

  useEffect(() => {
    if (!playing) return;

    let frame = 0;
    let last = performance.now();

    const tick = (now: number) => {
      const next = Math.min(at.current + (now - last) * rate.current, axis.length);

      last = now;
      at.current = next;
      setPosition(next);

      if (next >= axis.length) {
        setPlaying(false);
        return;
      }

      frame = requestAnimationFrame(tick);
    };

    frame = requestAnimationFrame(tick);

    return () => cancelAnimationFrame(frame);
  }, [playing, axis.length]);

  // The address follows the playhead whenever it comes to rest, so the moment on screen is
  // a link somebody can send a colleague.
  useEffect(() => {
    if (playing) return;

    onMoment(Math.round((fromAxis(axis, position) - axis.start) / 1000));
  }, [playing, position, axis, onMoment]);

  /**
   * Moves the playhead. The ref moves at once, not after the next render, so that a key held
   * down — or three presses in one breath — each start from where the last one landed.
   */
  const jump = useCallback(
    (next: number) => {
      const clamped = Math.min(Math.max(next, 0), axis.length);

      at.current = clamped;
      setPosition(clamped);
    },
    [axis.length],
  );

  const seekReal = useCallback((moment: number) => jump(toAxis(axis, moment)), [axis, jump]);

  const toggle = useCallback(() => {
    // Play from the start again once it has ended, as every player does.
    if (!playing && at.current >= axis.length) jump(0);

    setPlaying((now) => !now);
  }, [axis.length, jump, playing]);

  const act = useCallback(
    (action: ReturnType<typeof actionFor>) => {
      const moment = fromAxis(axis, at.current);

      switch (action) {
        case 'toggle':
          toggle();
          break;
        case 'back':
          seekReal(moment - STEP_SECONDS * 1000);
          break;
        case 'forward':
          seekReal(moment + STEP_SECONDS * 1000);
          break;
        case 'previousChapter': {
          const target = previousTarget(targets, moment);
          if (target !== null) seekReal(target);
          break;
        }
        case 'nextChapter': {
          const target = nextTarget(targets, moment);
          if (target !== null) seekReal(target);
          break;
        }
        case 'slower':
          setSpeed((s) => slower(s));
          break;
        case 'faster':
          setSpeed((s) => faster(s));
          break;
        case 'follow':
          setFollow((f) => !f);
          break;
        case 'start':
          jump(0);
          break;
        case 'end':
          jump(axis.length);
          break;
        default:
          break;
      }
    },
    [axis, targets, seekReal, toggle, jump],
  );

  useEffect(() => {
    const busy =
      'input, textarea, select, button, [role="radio"], [role="switch"], [contenteditable="true"]';

    const onKey = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null;

      if (target?.closest(busy) || event.altKey || event.ctrlKey || event.metaKey) return;
      if (document.querySelector('dialog[open]')) return;

      const action = actionFor(event.key, event.shiftKey);

      if (action === null) return;

      event.preventDefault();
      act(action);
    };

    window.addEventListener('keydown', onKey);

    return () => window.removeEventListener('keydown', onKey);
  }, [act]);

  // --- The call, in step ----------------------------------------------------------

  const audio = useRef<HTMLAudioElement>(null);
  const held = useRef<{ callId: string; link: HeldLink } | null>(null);
  const fetching = useRef(false);

  const active = journey.calls.find((call) => recordingOffset(call, real) !== null) ?? null;
  const offset = active === null ? null : recordingOffset(active, real);
  const audible = playing && speed <= AUDIBLE_UP_TO && active !== null;
  const second = offset === null ? null : Math.floor(offset);

  useEffect(() => {
    const element = audio.current;

    if (element === null) return;

    if (!audible || active === null || second === null) {
      if (!element.paused) element.pause();
      return;
    }

    if (fetching.current) return;

    void (async () => {
      try {
        if (held.current?.callId !== active.callId || !isFresh(held.current.link, Date.now())) {
          fetching.current = true;

          const answer = await callsApi.recording(active.callId);

          held.current = {
            callId: active.callId,
            link: {
              url: answer.url,
              fetchedAt: Date.now(),
              expiresInSeconds: answer.expiresInSeconds,
            },
          };
          element.src = answer.url;
        }

        const into = recordingOffset(active, fromAxis(axis, at.current)) ?? second;

        if (Math.abs(element.currentTime - into) > 0.8) element.currentTime = into;

        element.playbackRate = rate.current;

        if (element.paused) await element.play();
      } catch {
        // A recording past its retention, or a browser that would not play: the replay
        // goes on without the sound rather than stopping.
      } finally {
        fetching.current = false;
      }
    })();
  }, [audible, active, second, axis]);

  /** Hear one call alone: from where it was answered, at normal speed. */
  const hear = (call: JourneyCall) => {
    if (!call.answeredAt) return;

    setSpeed(1);
    seekReal(Date.parse(call.answeredAt));
    setPlaying(true);
  };

  // --- The printed sheet ----------------------------------------------------------

  const camera = useRef<(() => string | null) | null>(null);
  const [snapshot, setSnapshot] = useState<string | null>(null);
  const [printing, setPrinting] = useState(false);

  const onCamera = useCallback((shoot: () => string | null) => {
    camera.current = shoot;
  }, []);

  useEffect(() => {
    if (!printing) return;

    // One frame for the sheet to take the picture, then the browser's own print dialog.
    const frame = requestAnimationFrame(() => {
      window.print();
      setPrinting(false);
    });

    return () => cancelAnimationFrame(frame);
  }, [printing]);

  const [gpxProblem, setGpxProblem] = useState<string | null>(null);

  const drivers = journey.legs.map((leg) =>
    [leg.driverName, leg.callSign ? `· ${leg.callSign}` : null, leg.plate ? `· ${leg.plate}` : null]
      .filter(Boolean)
      .join(' '),
  );

  return (
    // Its own stack, so every gap on the page — between the order, the banners, the cards and
    // the calls — is the one the grid uses.
    <div className={LAYOUT.page}>
      <Card>
        <div className="flex items-center gap-4 flex-wrap px-4 py-3">
          <Button size="sm" onClick={onBack}>
            <Icon name="back" small />
            {t('reports.journeys.player.back')}
          </Button>

          <div className="flex flex-col gap-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-page font-semibold font-mono tracking-[0.04em]">
                {journey.reference}
              </span>
              <Pill soft tone={stateTone(journey.state)}>
                {t(`states.${journey.state}`, { defaultValue: journey.state })}
              </Pill>
              {journey.problems.map((problem) => (
                <Pill key={problem} tone={problemTone(problem)}>
                  {t(`reports.journeys.problems.${problem}`)}
                </Pill>
              ))}
            </div>
            <Sub block>
              {[
                formats.moment(journey.requestedAt),
                journey.customerName ?? journey.customerPhone ?? t('reports.journeys.anonymous'),
                journey.customerName && journey.customerPhone ? journey.customerPhone : null,
                t(`reports.journeys.source.${sourceWord(journey.source)}`),
              ]
                .filter(Boolean)
                .join(' · ')}
            </Sub>
            <Sub block>
              {drivers.length > 0 ? drivers.join(' → ') : t('reports.journeys.noDriver')}
            </Sub>
          </div>

          <div className="flex items-center gap-2 ml-auto">
            <Button
              disabled={!hasTrack}
              title={t('reports.journeys.player.gpxHelp')}
              onClick={() => {
                setGpxProblem(null);
                downloadJourney(journey.orderId).catch((error: unknown) =>
                  setGpxProblem(describe(error, t, 'settings.couldNotLoad')),
                );
              }}
            >
              <Icon name="download" small />
              {t('reports.journeys.player.gpx')}
            </Button>
            <Button
              onClick={() => {
                setSnapshot(camera.current?.() ?? null);
                setPrinting(true);
              }}
            >
              <Icon name="print" small />
              {t('reports.journeys.player.print')}
            </Button>
          </div>
        </div>
      </Card>

      {gpxProblem !== null && <Banner tone="danger">{gpxProblem}</Banner>}

      {journey.recordedBefore && (
        <Banner tone="warn">{t('reports.journeys.player.recordedBefore')}</Banner>
      )}

      {journey.beingWritten && (
        <Banner
          tone="warn"
          actions={
            <Button size="sm" onClick={onAskAgain}>
              {t('reports.journeys.player.askAgain')}
            </Button>
          }
        >
          {t('reports.journeys.player.beingWritten')}
        </Banner>
      )}

      {!hasTrack && !journey.recordedBefore && !journey.beingWritten && (
        <Banner tone="warn">
          {journey.chapters.some((c) => c.kind === 'approach')
            ? t('reports.journeys.player.noTrack')
            : t('reports.journeys.player.neverTaken')}
        </Banner>
      )}

      <div className={LAYOUT.grid}>
        <div className={LAYOUT.column}>
          <Card className={LAYOUT.map}>
            <div className={cx('relative min-h-[420px]', LAYOUT.mapHeight)}>
              <Suspense fallback={<EmptyState>{t('app.loading')}</EmptyState>}>
                <JourneyMap
                  journey={journey}
                  at={real}
                  follow={follow && playing}
                  onCamera={onCamera}
                />
              </Suspense>

              <div className="absolute top-3 left-3 flex flex-col gap-0.5 px-3 py-2 bg-surface border border-rule rounded-card shadow-float pointer-events-none">
                <span className="text-section font-semibold tabular-nums">
                  {formats.timeWithSeconds(new Date(real).toISOString())}
                </span>
                <span className="text-secondary text-ink-2">
                  {chapter
                    ? t(`reports.journeys.chapter.${chapter.kind}`)
                    : t('reports.journeys.chapter.before')}
                  {car?.speedKmh != null &&
                    ` · ${t('reports.journeys.player.speed', { speed: Math.round(car.speedKmh) })}`}
                </span>
                {journey.legs.length > 1 && car !== null && (
                  <span className="text-secondary text-ink-3">{drivers[car.leg]}</span>
                )}
                {car?.silent && (
                  <Pill tone="neutral" dot>
                    {t('reports.journeys.player.noSignal')}
                  </Pill>
                )}
                {active !== null && playing && (
                  <Pill tone="brand" dot>
                    {audible
                      ? t('reports.journeys.player.hearing')
                      : t('reports.journeys.player.callMuted')}
                  </Pill>
                )}
              </div>
            </div>
          </Card>

          <Card className={LAYOUT.controls}>
            <div className="flex flex-col gap-3 px-4 py-3">
              <div className="flex items-center gap-3 flex-wrap">
                <div className="flex items-center gap-1.5">
                  <Button
                    icon
                    size="sm"
                    title={t('reports.journeys.player.previous')}
                    aria-label={t('reports.journeys.player.previous')}
                    onClick={() => act('previousChapter')}
                  >
                    <Icon name="skip-back" small />
                  </Button>
                  <Button
                    icon
                    size="sm"
                    title={t('reports.journeys.player.back10')}
                    aria-label={t('reports.journeys.player.back10')}
                    onClick={() => act('back')}
                  >
                    <Icon name="rewind" small />
                  </Button>
                  <Button
                    variant="primary"
                    onClick={toggle}
                    aria-label={
                      playing
                        ? t('reports.journeys.player.pause')
                        : t('reports.journeys.player.play')
                    }
                  >
                    <Icon name={playing ? 'pause' : 'play'} small />
                    {playing
                      ? t('reports.journeys.player.pause')
                      : t('reports.journeys.player.play')}
                  </Button>
                  <Button
                    icon
                    size="sm"
                    title={t('reports.journeys.player.forward10')}
                    aria-label={t('reports.journeys.player.forward10')}
                    onClick={() => act('forward')}
                  >
                    <Icon name="fast-forward" small />
                  </Button>
                  <Button
                    icon
                    size="sm"
                    title={t('reports.journeys.player.next')}
                    aria-label={t('reports.journeys.player.next')}
                    onClick={() => act('nextChapter')}
                  >
                    <Icon name="skip-forward" small />
                  </Button>
                </div>

                <Segmented<string>
                  label={t('reports.journeys.player.speedLabel')}
                  value={String(speed)}
                  compact
                  onChange={(value) => setSpeed(Number(value))}
                  options={SPEEDS.map((s) => ({
                    value: String(s),
                    label: `${s}×`,
                  }))}
                />

                <Button
                  size="sm"
                  variant={follow ? 'primary' : 'outline'}
                  aria-pressed={follow}
                  title={t('reports.journeys.player.followHelp')}
                  onClick={() => setFollow((f) => !f)}
                >
                  <Icon name="follow" small />
                  {t('reports.journeys.player.follow')}
                </Button>

                <span className="ml-auto text-table tabular-nums text-ink-2">
                  {elapsed(real, axis.start)}
                  <span className="text-ink-3"> / {elapsed(axis.end, axis.start)}</span>
                </span>
              </div>

              {/* No gap between them, so the playhead runs from one into the other as a line. */}
              <div>
                <JourneyTimeline journey={journey} axis={axis} position={position} onSeek={jump} />
                <SpeedChart journey={journey} axis={axis} real={real} onSeek={seekReal} />
              </div>

              <p className="m-0 text-secondary text-ink-3">{t('reports.journeys.player.keys')}</p>
            </div>
          </Card>
        </div>

        <div className={LAYOUT.column}>
          <WhatHappened journey={journey} real={real} onSeek={seekReal} className={LAYOUT.events} />
          <JourneyOrder journey={journey} onSeek={seekReal} className={LAYOUT.order} />
        </div>

        <div className={LAYOUT.column}>
          <Measures journey={journey} className={LAYOUT.measures} />
          <JourneyFare journey={journey} className={LAYOUT.fare} />
          <JourneySpeed journey={journey} onSeek={seekReal} className={LAYOUT.speed} />
        </div>
      </div>

      {journey.calls.length > 0 && (
        <Calls
          calls={journey.calls}
          active={active?.callId ?? null}
          onHear={hear}
          onSeek={seekReal}
        />
      )}

      {/* Never shown on screen; it is what the printer gets. */}
      <audio ref={audio} preload="none" className="hidden" />
      <JourneySheet journey={journey} snapshot={snapshot} />
    </div>
  );
}

/** Expected beside actual: the arrival, the wait, the two drives. The fare has its own card. */
function Measures({ journey, className }: { journey: JourneyDetail; className?: string }) {
  const { t } = useTranslation();
  const formats = useTenantFormats();
  const m = journey.measures;

  const late = m.lateSeconds;
  const rows: { label: string; value: ReactNode }[] = [];

  const near = (metres: number) => distanceWords(t, formats, metres);

  if (m.acceptedAt)
    rows.push({
      label: t('reports.journeys.measures.accepted'),
      value: formats.timeWithSeconds(m.acceptedAt),
    });

  if (m.expectedAt) {
    rows.push({
      label: t('reports.journeys.measures.expected'),
      value: (
        <>
          {formats.timeWithSeconds(m.expectedAt)}
          <Sub block>{t(`reports.journeys.measures.basis.${m.expectedBasis ?? 'routed'}`)}</Sub>
        </>
      ),
    });
  }

  if (m.arrivedAt || late != null) {
    rows.push({
      label: t('reports.journeys.measures.arrived'),
      value: (
        <>
          {m.arrivedAt
            ? formats.timeWithSeconds(m.arrivedAt)
            : t('reports.journeys.measures.neverArrived')}
          {late != null && (
            <Sub
              block
              className={late >= journey.thresholds.lateMinutes * 60 ? 'text-danger' : undefined}
            >
              {late >= 0
                ? t('reports.journeys.measures.late', {
                    time: formats.duration(late),
                  })
                : t('reports.journeys.measures.early', {
                    time: formats.duration(-late),
                  })}
            </Sub>
          )}
        </>
      ),
    });
  }

  if (m.stoodFromPickupMetres != null) {
    rows.push({
      label: t('reports.journeys.measures.stood'),
      value: t('reports.journeys.measures.fromPin', {
        distance: near(m.stoodFromPickupMetres),
      }),
    });
  }

  if (m.waitSeconds != null) {
    rows.push({
      label: t('reports.journeys.measures.waited'),
      value: (
        <span
          className={
            m.waitSeconds >= journey.thresholds.longWaitMinutes * 60 ? 'text-warn' : undefined
          }
        >
          {formats.duration(m.waitSeconds)}
        </span>
      ),
    });
  }

  if (m.approachDrivenMetres != null || m.approachRecommendedMetres != null) {
    rows.push({
      label: t('reports.journeys.measures.approach'),
      value: compare(t, formats, m.approachDrivenMetres, m.approachRecommendedMetres, null),
    });
  }

  if (m.rideDrivenMetres != null || m.rideRecommendedMetres != null) {
    rows.push({
      label: t('reports.journeys.measures.ride'),
      value: compare(
        t,
        formats,
        m.rideDrivenMetres,
        m.rideRecommendedMetres,
        m.detourPercent ?? null,
      ),
    });
  }

  if (m.rideSeconds != null) {
    rows.push({
      label: t('reports.journeys.measures.rideTime'),
      value: (
        <>
          {formats.duration(m.rideSeconds)}
          {m.rideRecommendedSeconds != null && (
            <Sub block>
              {t('reports.journeys.measures.routerSays', {
                time: formats.duration(m.rideRecommendedSeconds),
              })}
            </Sub>
          )}
        </>
      ),
    });
  }

  if (m.endedFromDestinationMetres != null) {
    rows.push({
      label: t('reports.journeys.measures.ended'),
      value: t('reports.journeys.measures.fromDestination', {
        distance: near(m.endedFromDestinationMetres),
      }),
    });
  }

  return (
    <Card className={cx('flex flex-col', className)}>
      <CardHeader title={t('reports.journeys.measures.title')} />
      <div className="min-h-0 overflow-y-auto">
        {rows.length === 0 ? (
          <EmptyState>{t('reports.journeys.measures.none')}</EmptyState>
        ) : (
          <dl className="grid grid-cols-[minmax(140px,auto)_1fr] gap-x-4 gap-y-2 px-4 py-3 m-0 text-table">
            {rows.map((row) => (
              <div key={row.label} className="contents">
                <dt className="text-ink-3 m-0">{row.label}</dt>
                <dd className="m-0 min-w-0 tabular-nums">{row.value}</dd>
              </div>
            ))}
          </dl>
        )}
      </div>
    </Card>
  );
}

function compare(
  t: (key: string, options?: Record<string, unknown>) => string,
  formats: ReturnType<typeof useTenantFormats>,
  driven: number | null | undefined,
  recommended: number | null | undefined,
  detour: number | null,
) {
  return (
    <>
      {driven != null ? distanceWords(t, formats, driven) : '—'}
      {recommended != null && (
        <Sub block>
          {t('reports.journeys.measures.recommended', {
            distance: distanceWords(t, formats, recommended),
          })}
          {detour != null && ` · ${detour > 0 ? '+' : ''}${formats.count(Math.round(detour))}%`}
        </Sub>
      )}
    </>
  );
}

/** Everything that happened, in order, each line a place to jump to. */
function WhatHappened({
  journey,
  real,
  onSeek,
  className,
}: {
  journey: JourneyDetail;
  real: number;
  onSeek: (moment: number) => void;
  className?: string;
}) {
  const { t } = useTranslation();
  const formats = useTenantFormats();

  // The firm's reasons, as the order card reads them, only for a passenger's cancellation.
  const firmReasons = useCancellationReasonNames(
    journey.events.some((event) => isRiderReason(event.reason)),
  );

  const lines: (JourneyEvent & { call?: JourneyCall })[] = [
    ...journey.events,
    ...journey.calls.map((call) => ({
      at: call.startedAt,
      kind: call.direction === 'Outbound' ? 'callOut' : 'callIn',
      actor: null,
      actorUserId: null,
      actorName: call.answeredByName ?? null,
      driverId: null,
      driverName: null,
      reason: null,
      distanceMetres: null,
      automatic: null,
      stars: null,
      text: null,
      call,
    })),
  ].sort((a, b) => Date.parse(a.at) - Date.parse(b.at));

  // The line the playhead has passed most recently.
  let current = -1;
  lines.forEach((line, index) => {
    if (Date.parse(line.at) <= real) current = index;
  });

  return (
    <Card className={cx('flex flex-col', className)}>
      <CardHeader title={t('reports.journeys.events.title')} />
      <ol
        className={cx(
          'm-0 p-0 list-none max-h-[420px] overflow-y-auto min-[1800px]:max-h-none',
          'min-[1800px]:flex-1 min-[1800px]:min-h-0',
        )}
      >
        {lines.map((line, index) => (
          <li key={`${line.kind}-${line.at}-${index}`}>
            <button
              type="button"
              className={cx(
                'w-full grid grid-cols-[72px_10px_minmax(0,1fr)] items-baseline gap-2 px-4 py-1.5 text-left text-table cursor-pointer border-b border-rule-soft hover:bg-surface-2',
                index === current && 'bg-brand-soft',
              )}
              onClick={() => onSeek(Date.parse(line.at))}
            >
              <time dateTime={line.at} className="text-ink-3 tabular-nums">
                {formats.timeWithSeconds(line.at)}
              </time>
              <span
                className={cx(
                  'w-2 h-2 rounded-full self-center',
                  {
                    ok: 'bg-ok',
                    warn: 'bg-warn',
                    danger: 'bg-danger',
                    info: 'bg-info',
                    neutral: 'bg-ink-3',
                    brand: 'bg-brand',
                  }[line.call ? 'brand' : eventTone(line.kind)],
                )}
              />
              <span className="min-w-0">
                {t(`reports.journeys.events.${line.kind}`, {
                  driver: line.driverName ?? t('reports.journeys.someDriver'),
                  who:
                    line.actorName ?? t(`actors.${line.actor ?? 'System'}`, { defaultValue: '' }),
                  stars: line.stars ?? 0,
                  address: line.text ?? '',
                })}
                <Sub>
                  {[
                    line.distanceMetres != null ? formats.distance(line.distanceMetres) : null,
                    line.automatic === true ? t('reports.journeys.automatic') : null,
                    eventReason(t, line.kind, line.reason, firmReasons),
                    line.kind === 'rated' && line.text ? `“${line.text}”` : null,
                    line.actorName && line.kind !== 'callIn' && line.kind !== 'callOut'
                      ? line.actorName
                      : null,
                  ]
                    .filter(Boolean)
                    .map((part) => ` · ${part}`)
                    .join('')}
                </Sub>
              </span>
            </button>
          </li>
        ))}
      </ol>
    </Card>
  );
}

/** The order's calls, each one click from being heard where it happened. */
function Calls({
  calls,
  active,
  onHear,
  onSeek,
}: {
  calls: readonly JourneyCall[];
  active: string | null;
  onHear: (call: JourneyCall) => void;
  onSeek: (moment: number) => void;
}) {
  const { t } = useTranslation();
  const formats = useTenantFormats();

  return (
    <Card>
      <CardHeader title={t('reports.journeys.calls.title')} />
      <ul className="m-0 p-0 list-none">
        {calls.map((call) => (
          <li
            key={call.callId}
            className={cx(
              'flex items-center gap-3 px-4 py-2 border-b border-rule-soft text-table',
              call.callId === active && 'bg-brand-soft',
            )}
          >
            <Button
              icon
              size="sm"
              variant="outline"
              title={t('reports.journeys.calls.jump')}
              aria-label={t('reports.journeys.calls.jump')}
              onClick={() => onSeek(Date.parse(call.startedAt))}
            >
              <Icon name="phone" small />
            </Button>
            <span className="tabular-nums">{formats.timeWithSeconds(call.startedAt)}</span>
            <span>{t(`reports.calls.direction.${call.direction}`)}</span>
            <span className="tabular-nums text-ink-2">
              {call.number ?? t('reports.calls.withheld')}
            </span>
            {call.answeredByName && <Sub>{call.answeredByName}</Sub>}
            {call.announcedEtaMinutes != null && (
              <Pill soft tone="info">
                {t('reports.journeys.calls.announced', {
                  count: call.announcedEtaMinutes,
                })}
              </Pill>
            )}
            <span className="ml-auto flex items-center gap-2">
              {call.hasRecording ? (
                <Button size="sm" onClick={() => onHear(call)}>
                  <Icon name="play" small />
                  {t('reports.journeys.calls.hear', {
                    time: formats.duration(call.recordingSeconds ?? null),
                  })}
                </Button>
              ) : (
                <Sub>{t('reports.journeys.calls.notRecorded')}</Sub>
              )}
            </span>
          </li>
        ))}
      </ul>
    </Card>
  );
}
